/* ====================
    IMPORTS & PACKAGES
   ==================== */
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, onSnapshot, query, orderBy } from "firebase/firestore";
import {   Users, BookOpen, DollarSign, Activity, Bell, LogOut, CheckSquare, Award, User, PlusCircle, TrendingUp, Landmark } from "lucide-react";

export default function Sidebar({ sidebarOpen, setSidebarOpen, activeTab, setActiveTab, role, userData, handleLogout }) {
  
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  /* ===================================
      LIFECYCLE EFFECTS & DATA WATCHERS
     =================================== */

  useEffect(() => {
    if (!userData) return;

    let unsubscribe = () => {};

    if (role === "student") {
      const q = query(collection(db, "announcements"), orderBy("timestamp", "desc"));
      unsubscribe = onSnapshot(q, (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const enrollments = userData.enrollments || [];
        const myEnrolledCourseCodes = enrollments.map(en => en.courseCode);
        
          const studentList = list.filter(n => {
            if (n.targetType === "personal_student") {
              return n.targetStudentUid === userData.uid;
            }
            if (n.targetType === "all" || n.targetRole === "student") return true;
          if (n.targetType === "course" || n.targetType === "students_course") {
            return myEnrolledCourseCodes.includes(n.targetCourseId);
          }
          if (n.targetType === "batch") {
            return enrollments.some(en => en.courseCode === n.targetCourseId && en.batchName === n.targetBatchId);
          }
          return false;
        });

        const persistentReads = userData.readNotifications || [];
        setUnreadNotifCount(studentList.filter(n => !persistentReads.includes(n.id)).length);
      });
    } else if (role === "admin") {
      const q = query(collection(db, "auditLogs"));
      unsubscribe = onSnapshot(q, (snap) => {
        const persistentReads = userData.readAudits || [];
        const unread = snap.docs.map(d => d.id).filter(id => !persistentReads.includes(id)).length;
        setUnreadNotifCount(unread);
      });
    }

    return () => unsubscribe();
  }, [userData, activeTab]);

  /* =======================================
      UNREAD NOTIFICATIONS QUANTIFY HELPERS
     ======================================= */

  /* Fetch Unread Student Announcements Count  */
  async function fetchStudentUnreadCount() {
    try {
      const snap = await getDocs(collection(db, "announcements"));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const studentList = list.filter(n => 
        n.targetType === "all" || 
        n.targetRole === "student" || 
        n.targetCourseId === userData.assignedCourse
      );
      const persistentReads = userData.readNotifications || [];
      setUnreadNotifCount(studentList.filter(n => !persistentReads.includes(n.id)).length);
    } catch (e) {
      console.error(e);
    }
  }

  /* Fetch Unread Admin Security Audits Count */
  async function fetchAdminUnreadAudits() {
    try {
      const snap = await getDocs(collection(db, "auditLogs"));
      const persistentReads = userData.readAudits || [];
      const unread = snap.docs.map(d => d.id).filter(id => !persistentReads.includes(id)).length;
      setUnreadNotifCount(unread);
    } catch (e) {
      console.error(e);
    }
  }

  /* ================================
      ROLE-BASED SIDEBAR MENUS SETUP
     ================================ */

  const getTabsByRole = () => {
    if (role === "admin") {
      return [
        { id: "approvals", label: "Registry & Approvals", icon: <Users size={18} /> },
        { id: "courses", label: "Course & Batch Setup", icon: <BookOpen size={18} /> },
        { id: "bank", label: "Bank Setup", icon: <Landmark size={18} /> },
        { id: "fees", label: "Fee Ledger", icon: <DollarSign size={18} /> },
        { id: "announcements", label: "Announcements", icon: <Bell size={18} /> },
        { id: "audits", label: "Security Audits", icon: <Activity size={18} /> }
      ];
    } else if (role === "instructor") {
      return [
        { id: "dashboard", label: "Instructor Home", icon: <TrendingUp size={18} /> },
        { id: "materials", label: "Course Resources", icon: <BookOpen size={18} /> },
        { id: "attendance", label: "Attendance Ledger", icon: <CheckSquare size={18} /> },
        { id: "assessments", label: "Assessments Ledger", icon: <PlusCircle size={18} /> },
        { id: "grading", label: "Submission Grader", icon: <Award size={18} /> },        
        { id: "announcements", label: "Class Announcements", icon: <Bell size={18} /> }
      ];
    } else {
      return [
        { id: "dashboard", label: "Dashboard Overview", icon: <Activity size={18} /> },
        { id: "materials", label: "Lessons & Submissions", icon: <BookOpen size={18} /> },
        { id: "fees", label: "Fee Balance", icon: <DollarSign size={18} /> },
        { id: "announcements", label: "Announcements", icon: <Bell size={18} /> },
        { id: "profile", label: "Manage Profile", icon: <User size={18} /> }
      ];
    }
  };

  const menuItems = getTabsByRole();

  /* ===========================
      SIDEBAR VIEWPORT RENDERER
     =========================== */
  return (
    <aside className={`sidebar ${sidebarOpen ? "active" : ""}`}>
      
      {/* Sidebar Branding & User Profile Banner */}
      <div className="sidebar-brand">        
          <img src="/logo192.png" alt="Logo" style={{ width: "50px", height: "auto", borderRadius: "40%", background: "#b7cbf7",  }} />          
        <span>Oncode APT System</span>
      </div>

      <div style={{ padding: "0.75rem 0", borderBottom: "1px solid #1e293b", display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <div style={{ background: "var(--navy-primary)", padding: "0.5rem", borderRadius: "50%", color: "white", display: "flex" }}>
          <User size={18} />
        </div>
        <div>
          <h4 style={{ margin: 0, fontSize: "0.9rem", color: "white" }}>{userData?.fullName}</h4>
          <small style={{ color: "#94a3b8" }}>{userData?.customId || "Pending ID"}</small>
        </div>
      </div>

      {/* Dynamic Sidebar Items List */}
      <ul className="sidebar-menu" style={{ flex: 1 }}>
        {menuItems.map((item) => (
          <li
            key={item.id}
            className={`sidebar-item ${activeTab === item.id ? "active" : ""}`}
            onClick={() => {
              setActiveTab(item.id);
              setSidebarOpen(false);
            }}
            style={{ position: "relative" }}
          >
            {item.icon}
            <span>{item.label}</span>
            {item.badge > 0 && (
              <span style={{ position: "absolute", right: "1.5rem", background: "var(--danger)", color: "white", borderRadius: "50%", fontSize: "0.65rem", padding: "2px 6px", fontWeight: "bold" }}>
                {item.badge}
              </span>
            )}
          </li>
        ))}
      </ul>

      {/* Sign Out Action Button */}
      <button 
        className="navy-btn navy-btn-danger" 
        onClick={handleLogout} 
        style={{ width: "100%", justifyContent: "center", marginTop: "auto" }}
      >
        <LogOut size={18} /> Exit Oncode APT Sytem
      </button>
    </aside>
  );
}